/**
 * The birth moment as the one birth component holds it, and the rules it
 * shares with the server (Mystic-Aida tarot/service/birth_moment.py):
 * a date (not in the future, at least MIN_AGE years ago), a time that is a
 * part of the day, an exact clock, or "Not sure" (noon, and it says so), and
 * a place. Pure; the component and its specs both lean on it.
 */

// Keep in step with tarot/service/birth_windows.py. They cover the day.
export const WINDOWS = [
  { key: 'early_morning', label: 'Early morning', hint: 'midnight to 7am', mid: '03:30' },
  { key: 'morning', label: 'Morning', hint: '7am to noon', mid: '09:30' },
  { key: 'afternoon', label: 'Afternoon', hint: 'noon to 5pm', mid: '14:30' },
  { key: 'evening', label: 'Evening', hint: '5pm to 9pm', mid: '19:00' },
  { key: 'late_evening', label: 'Late evening', hint: '9pm to midnight', mid: '22:30' },
]

// The Terms (mysticaida.ai/terms) say 18.
export const MIN_AGE = 18

export const ALL_FIELDS = ['date', 'time', 'place']

export function emptyForm () {
  return { birthdate: '', timeKind: '', windowKey: '', exactTime: '', place: null }
}

/** A moment from GET /birth/moment/ (or null) into the form. */
export function formFromMoment (m) {
  const f = emptyForm()
  if (!m) return f
  f.birthdate = m.birthdate || ''
  const t = m.time || null
  if (t && t.kind === 'window') { f.timeKind = 'window'; f.windowKey = t.value || '' }
  else if (t && t.kind === 'exact') { f.timeKind = 'exact'; f.exactTime = t.value || '' }
  else if (t && t.kind === 'unknown') f.timeKind = 'unknown'
  f.place = m.place ? { ...m.place } : null
  return f
}

function parseDay (s) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s || '')) return null
  const [y, m, d] = s.split('-').map(Number)
  const dt = new Date(y, m - 1, d)
  return (dt.getFullYear() === y && dt.getMonth() === m - 1 && dt.getDate() === d) ? dt : null
}

function ageOn (born, today) {
  let age = today.getFullYear() - born.getFullYear()
  if (today.getMonth() < born.getMonth() || (today.getMonth() === born.getMonth() && today.getDate() < born.getDate())) age -= 1
  return age
}

/**
 * Field errors ({birthdate?, time?, place?}); empty when it's good to send.
 * `optional`: fields that may be left empty (the gift room's birthday card:
 * the date, then a time and a place each savable alone, gift bug walk
 * 2026-10-25). A half-filled optional field is still checked.
 */
export function validate (form, { today = new Date(), fields = ALL_FIELDS, optional = [] } = {}) {
  const skip = (f) => optional.includes(f) && (f === 'time' ? !form.timeKind : !form.place)
  fields = fields.filter((f) => !skip(f))
  const e = {}
  if (fields.includes('date')) {
    const born = parseDay(form.birthdate)
    const t0 = new Date(today.getFullYear(), today.getMonth(), today.getDate())
    if (!born) e.birthdate = 'Please enter your birth date.'
    else if (born > t0) e.birthdate = 'That date is in the future.'
    else if (ageOn(born, t0) < MIN_AGE) e.birthdate = `You need to be at least ${MIN_AGE} to use Mystic AIda.`
    else if (born.getFullYear() < 1900) e.birthdate = 'Please check the year.'
  }
  if (fields.includes('time')) {
    if (form.timeKind === 'window') {
      if (!WINDOWS.some((w) => w.key === form.windowKey)) e.time = 'Pick a part of the day.'
    } else if (form.timeKind === 'exact') {
      const m = /^(\d{1,2}):(\d{2})$/.exec(form.exactTime || '')
      if (!m || Number(m[1]) > 23 || Number(m[2]) > 59) e.time = 'Please enter the time as HH:MM.'
    } else if (form.timeKind !== 'unknown') {
      e.time = 'Pick a time of day, an exact time, or "Not sure".'
    }
  }
  if (fields.includes('place')) {
    const p = form.place
    if (!p || p.latitude == null || p.longitude == null) e.place = 'Pick your birthplace.'
  }
  return e
}

export function timePayload (form) {
  if (form.timeKind === 'window') return { kind: 'window', value: form.windowKey }
  if (form.timeKind === 'exact') return { kind: 'exact', value: form.exactTime }
  if (form.timeKind === 'unknown') return { kind: 'unknown' }
  return null
}

/** The body POST /birth/moment/ takes (session_id added by the caller). */
export function toPayload (form) {
  const out = { birthdate: form.birthdate, time: timePayload(form) }
  if (form.place && form.place.latitude != null) {
    out.place = { latitude: form.place.latitude, longitude: form.place.longitude }
    if (form.place.name) out.place.name = form.place.name
  }
  return out
}

/** HH:MM for surfaces still on the old shape (Studio's reader birth). */
export function legacyTime (form) {
  if (form.timeKind === 'exact') return form.exactTime || ''
  if (form.timeKind === 'window') return (WINDOWS.find((w) => w.key === form.windowKey) || {}).mid || ''
  if (form.timeKind === 'unknown') return '12:00'
  return ''
}

export function isComplete (form) {
  return !!(form.birthdate && form.timeKind && form.place && form.place.latitude != null)
}

export function timeLabel (form) {
  if (form.timeKind === 'window') return (WINDOWS.find((w) => w.key === form.windowKey) || {}).label || ''
  if (form.timeKind === 'exact') return form.exactTime
  if (form.timeKind === 'unknown') return 'Not sure'
  return ''
}

/** "Sun in Leo · Moon in Pisces or Aries · Rising likely Virgo or Libra". */
export function signsLine (signs) {
  if (!signs || !signs.sun) return ''
  const parts = [`Sun in ${signs.sun}`]
  const moon = signs.moon || signs.moon_text
  if (moon) parts.push(`Moon in ${moon}`)
  if (signs.rising) parts.push(`Rising ${signs.rising}`)
  else if (signs.rising_text) parts.push(`Rising likely ${signs.rising_text}`)
  return parts.join(' · ')
}

export function debounce (fn, ms = 250) {
  let t = null
  return (...args) => {
    clearTimeout(t)
    t = setTimeout(() => fn(...args), ms)
  }
}
